import { z } from 'zod';
import { ICP_CLASSIFICATION, ICP_REVIEW_CONFIDENCE_THRESHOLD } from '../constants/icp.js';

/**
 * OpenAI Structured Output contract for the TCPR ICP classifier.
 *
 * Source of truth: docs/tcpr-icp-classifier/02_tcpr_icp_classifier_schema.json
 * — with ONE deviation: `confidence`'s `minimum`/`maximum` keywords are removed
 * because OpenAI strict mode (json_schema, strict: true) rejects numeric
 * bound keywords with a 400. The 0–100 bound is enforced by the zod
 * post-validation schema below (validateIcpStructuredOutput) instead.
 * Semantics are unchanged.
 */
export const ICP_STRUCTURED_OUTPUT_JSON_SCHEMA = {
  name: 'tcpr_icp_classification',
  schema: {
    title: 'TCPR ICP Industry Classification',
    type: 'object',
    additionalProperties: false,
    properties: {
      company_identity: { type: ['string', 'null'] },
      company_domain: { type: ['string', 'null'] },
      primary_business: { type: ['string', 'null'] },
      industry: { type: ['string', 'null'] },
      classification: {
        type: 'string',
        enum: ['QUALIFIED', 'EXCLUDED_ICP', 'REVIEW_REQUIRED'],
      },
      reason: { type: 'string' },
      evidence: { type: 'string' },
      confidence: { type: 'integer' },
    },
    required: [
      'company_identity',
      'company_domain',
      'primary_business',
      'industry',
      'classification',
      'reason',
      'evidence',
      'confidence',
    ],
  },
  strict: true,
} as const;

/** Raw shape returned by the model (before policy post-rules). */
export const icpStructuredOutputSchema = z.object({
  company_identity: z.string().nullish(),
  company_domain: z.string().nullish(),
  primary_business: z.string().nullish(),
  industry: z.string().nullish(),
  classification: z.enum([
    ICP_CLASSIFICATION.QUALIFIED,
    ICP_CLASSIFICATION.EXCLUDED_ICP,
    ICP_CLASSIFICATION.REVIEW_REQUIRED,
  ]),
  reason: z.string().min(1, 'reason must be a non-empty string'),
  evidence: z.string().min(1, 'evidence must be a non-empty string'),
  // Bounds enforced here (strict mode cannot carry minimum/maximum).
  confidence: z.number().int().min(0).max(100),
});

export type IcpStructuredOutput = z.infer<typeof icpStructuredOutputSchema>;

/** Result after the policy post-rule (confidence < 80 final decision → REVIEW_REQUIRED). */
export type IcpFinalResult = IcpStructuredOutput & {
  /** True when the server-side confidence downgrade to REVIEW_REQUIRED was applied. */
  post_adjusted: boolean;
};

/**
 * Validate a raw model response and apply the policy default rule:
 * a QUALIFIED / EXCLUDED_ICP decision whose confidence is below
 * ICP_REVIEW_CONFIDENCE_THRESHOLD becomes REVIEW_REQUIRED.
 */
export function validateIcpStructuredOutput(raw: unknown): IcpFinalResult {
  const parsed = icpStructuredOutputSchema.parse(raw);
  const needsReview =
    parsed.classification !== ICP_CLASSIFICATION.REVIEW_REQUIRED &&
    parsed.confidence < ICP_REVIEW_CONFIDENCE_THRESHOLD;

  if (!needsReview) {
    return { ...parsed, post_adjusted: false };
  }
  return {
    ...parsed,
    classification: ICP_CLASSIFICATION.REVIEW_REQUIRED,
    reason: `policy-guard: confidence ${parsed.confidence} is below ${ICP_REVIEW_CONFIDENCE_THRESHOLD} for a final decision. ${parsed.reason}`,
    post_adjusted: true,
  };
}
